import './index.css';
import { useEffect, useRef, useState } from 'react';
import { Coins, FileText, Vote, Wallet } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api-client.js';
import { publicSourcePage } from '../../lib/public-source-page.js';
import { saveComparisonSelection } from '../../lib/comparison-selection.js';
import { useApiQuery } from '../../hooks.js';
import { money, number } from '../../format.js';
import { Button, DataNote, EmptyState, Loading, ProgressBar, RequestError, SelectField, SourceLink, TextField } from '../../components/ui.js';
import { PageHero } from '../../components/page-hero.js';

interface Selection {
  id: number;
  name: string;
  role: string;
  source?: string;
  party?: string | null;
  state?: string | null;
  photo?: string | null;
  source_url?: string | null;
}

interface CoverageValue { value: number | null; complete: boolean }
interface Compared extends Selection {
  source: string;
  expenses: CoverageValue & { records?: number; note?: string };
  monthly?: number | null;
  proposals?: number | null;
  votes?: number | null;
  counts: { proposicoes?: CoverageValue; votacoes?: CoverageValue; emendas?: CoverageValue };
  amendments?: { count: number | null; committed: number | null; paid: number | null; complete: boolean };
  mandate_years?: number[];
  indicator_coverage?: Record<string, boolean>;
  coverage_detail?: Partial<Record<'expenses' | 'proposals' | 'votes' | 'amendments', string>>;
  sources?: { expenses?: string | null; proposals?: string | null; votes?: string | null; amendments?: string | null };
}

interface Reference { house?: string; expenses?: number | null; proposals?: number | null; votes?: number | null; amendments_paid?: number | null }
interface CompareResponse { items: Compared[]; year: number | null; all_mandates?: boolean; note: string; reference?: Reference | null }
interface Options { years: number[] }
interface PoliticianSearchResponse { items: Selection[]; total: number; page: number; size: number }

type MetricKey = 'expenses' | 'proposals' | 'votes' | 'amendments';
interface MetricDefinition {
  key: MetricKey;
  title: string;
  description: string;
  getValue: (person: Compared) => number | null;
  format: (value: number | null) => string;
  complete: (person: Compared) => boolean;
  coverage: (person: Compared) => string;
  source: (person: Compared) => string | null | undefined;
  reference: (reference: Reference) => number | null | undefined;
  icon: typeof Wallet;
}

const currentYear = new Date().getFullYear();

function readSavedSelection(): Selection[] {
  if (typeof window === 'undefined') return [];
  try {
    const parsed = JSON.parse(window.sessionStorage.getItem('comparison') ?? '[]') as Selection[];
    return Array.isArray(parsed) ? parsed.filter((item) => Number.isInteger(item?.id) && item.id > 0).slice(0, 4) : [];
  } catch { return []; }
}

function readSelection(params: URLSearchParams): Selection[] {
  const rawIds = params.get('politicos') ?? params.get('ids');
  if (rawIds === null) return readSavedSelection();
  const savedById = new Map(readSavedSelection().map((person) => [person.id, person]));
  return [...new Set(rawIds.split(',').map(Number).filter((id) => Number.isInteger(id) && id > 0))]
    .slice(0, 4)
    .map((id) => savedById.get(id) ?? { id, name: `Político ${id}`, role: '' });
}

function toSearchParams(searchParams: URLSearchParams, selection: Selection[], year: number, allMandates: boolean) {
  const next = new URLSearchParams(searchParams);
  next.set('ano', String(year));
  next.set('todos_anos', allMandates ? 'sim' : 'nao');
  next.delete('year');
  next.delete('all_mandates');
  next.delete('ids');
  if (selection.length) next.set('politicos', selection.map((person) => person.id).join(','));
  else next.delete('politicos');
  return next;
}

function formatSource(source: string) {
  return source === 'senado' ? 'Senado Federal' : 'Câmara dos Deputados';
}

function makeMetrics(allMandates: boolean): MetricDefinition[] {
  return [
    {
      key: 'expenses', title: 'Despesas', description: 'Cota parlamentar e verba de gabinete',
      getValue: (person) => person.expenses?.value ?? null, format: (value) => money(value),
      complete: (person) => Boolean(person.expenses?.complete),
      coverage: (person) => allMandates ? person.coverage_detail?.expenses ?? 'Mandato efetivo não identificado' : person.expenses?.complete ? 'Cobertura anual coletada' : person.expenses?.note ?? 'Cobertura anual não confirmada',
      source: (person) => person.sources?.expenses, reference: (reference) => reference.expenses, icon: Wallet,
    },
    {
      key: 'proposals', title: 'Proposições', description: 'Projetos, requerimentos e outras proposições',
      getValue: (person) => person.counts?.proposicoes?.value ?? null, format: (value) => number(value),
      complete: (person) => Boolean(person.counts?.proposicoes?.complete),
      coverage: (person) => allMandates ? person.coverage_detail?.proposals ?? 'Mandato efetivo não identificado' : person.counts?.proposicoes?.complete ? 'Cobertura anual coletada' : 'Cobertura parcial ou não disponível',
      source: (person) => person.sources?.proposals, reference: (reference) => reference.proposals, icon: FileText,
    },
    {
      key: 'votes', title: 'Votações nominais', description: 'Votos registrados; não medem presença ou alinhamento partidário',
      getValue: (person) => person.counts?.votacoes?.value ?? null, format: (value) => number(value),
      complete: (person) => Boolean(person.counts?.votacoes?.complete),
      coverage: (person) => allMandates ? person.coverage_detail?.votes ?? 'Mandato efetivo não identificado' : person.counts?.votacoes?.complete ? 'Cobertura anual coletada' : 'Cobertura parcial ou não disponível',
      source: (person) => person.sources?.votes, reference: (reference) => reference.votes, icon: Vote,
    },
    {
      key: 'amendments', title: 'Emendas pagas', description: 'Valor pago registrado pela CGU',
      getValue: (person) => person.amendments?.paid ?? null, format: (value) => money(value),
      complete: (person) => Boolean(person.amendments?.complete),
      coverage: (person) => allMandates ? person.coverage_detail?.amendments ?? 'Mandato efetivo não identificado' : person.amendments?.complete ? 'Cobertura anual coletada' : person.amendments?.count == null ? 'Não disponível na consulta' : 'Importação parcial',
      source: (person) => person.sources?.amendments, reference: (reference) => reference.amendments_paid, icon: Coins,
    },
  ];
}

export function ComparisonPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selection, setSelection] = useState<Selection[]>(() => readSelection(searchParams));
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectionError, setSelectionError] = useState('');
  const hydrationAttempts = useRef(new Set<number>());

  const yearValue = Number(searchParams.get('ano') ?? searchParams.get('year'));
  const year = Number.isInteger(yearValue) && yearValue >= 1900 && yearValue <= 2200 ? yearValue : currentYear;
  const allMandates = ['sim', 'true', '1'].includes((searchParams.get('todos_anos') ?? searchParams.get('all_mandates') ?? '').toLowerCase());
  const ids = selection.map((person) => person.id).join(',');
  const period = allMandates ? 'all_mandates=true' : `year=${year}`;
  const query = useApiQuery<CompareResponse>(['comparison', ids, period], `/api/compare?ids=${encodeURIComponent(ids)}&${period}`, selection.length >= 2);
  const optionsQuery = useApiQuery<Options>(['options'], '/api/options');

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [search]);

  const pickerQuery = useApiQuery<PoliticianSearchResponse>(
    ['comparison-picker', debouncedSearch],
    `/api/politicians?q=${encodeURIComponent(debouncedSearch)}&current=false&page=1&size=8&sort=name`,
    pickerOpen && debouncedSearch.length >= 2,
  );

  useEffect(() => {
    const legacyParams = searchParams.has('year') || searchParams.has('all_mandates') || searchParams.has('ids');
    const missingLocalizedState = !searchParams.has('ano') || !searchParams.has('todos_anos');
    const idsMismatch = (searchParams.get('politicos') ?? '') !== ids;
    if (!legacyParams && !missingLocalizedState && !idsMismatch) return;
    setSearchParams(toSearchParams(searchParams, selection, year, allMandates), { replace: true });
  }, [allMandates, ids, searchParams, selection, setSearchParams, year]);

  useEffect(() => {
    const missingSource = selection.filter((person) => !person.source && !hydrationAttempts.current.has(person.id));
    if (!missingSource.length) return;
    let active = true;
    void Promise.all(missingSource.map(async (person): Promise<Selection | null> => {
      try {
        const profile = await api<Selection>(`/api/politicians/${person.id}?year=${year}`);
        return {
          id: profile.id, name: profile.name, role: profile.role,
          ...(profile.source ? { source: profile.source } : {}),
          ...(profile.party !== undefined ? { party: profile.party } : {}),
          ...(profile.state !== undefined ? { state: profile.state } : {}),
          ...(profile.photo !== undefined ? { photo: profile.photo } : {}),
          ...(profile.source_url !== undefined ? { source_url: profile.source_url } : {}),
        };
      } catch { return null; }
    })).then((resolved) => {
      if (!active) return;
      missingSource.forEach((person) => hydrationAttempts.current.add(person.id));
      const details = new Map<number, Selection>();
      resolved.forEach((person) => { if (person) details.set(person.id, person); });
      if (!details.size) return;
      setSelection((current) => {
        const next = current.map((person) => ({ ...person, ...(details.get(person.id) ?? {}) }));
        saveComparisonSelection(next);
        return next;
      });
    });
    return () => { active = false; };
  }, [ids, selection, year]);

  const updateSelection = (next: Selection[]) => {
    setSelection(next);
    setSelectionError('');
    saveComparisonSelection(next);
    setSearchParams((current) => toSearchParams(current, next, year, allMandates));
  };

  const remove = (id: number) => updateSelection(selection.filter((person) => person.id !== id));
  const clear = () => updateSelection([]);

  const addPerson = (person: Selection) => {
    if (selection.some((selected) => selected.id === person.id)) return;
    if (selection.length >= 4) {
      setSelectionError('A comparação permite até quatro políticos.');
      return;
    }
    const incompatible = selection.some((selected) => selected.source && person.source && (selected.source !== person.source || selected.role !== person.role));
    if (incompatible) {
      setSelectionError('Compare políticos do mesmo cargo e da mesma Casa legislativa.');
      return;
    }
    updateSelection([...selection, person]);
    setSearch('');
    setDebouncedSearch('');
    setPickerOpen(false);
  };

  const changePeriod = (value: string) => {
    const next = new URLSearchParams(searchParams);
    const isAllMandates = value === 'todos';
    if (!isAllMandates) next.set('ano', value);
    next.set('todos_anos', isAllMandates ? 'sim' : 'nao');
    next.delete('year');
    next.delete('all_mandates');
    setSearchParams(next);
  };

  const selectedValue = allMandates ? 'todos' : String(year);
  const years = [...new Set([currentYear, ...(optionsQuery.data?.years ?? [])])].sort((left, right) => right - left);
  const selectionById = new Map(selection.map((person) => [person.id, person]));
  const pickerResults = (pickerQuery.data?.items ?? []).filter((person) => !selectionById.has(person.id));
  const metrics = makeMetrics(allMandates);

  return <div className="comparison-page">
    <PageHero eyebrow="COMPARAÇÃO PARLAMENTAR" size="compact" title="Compare representantes" description="Escolha de dois a quatro políticos e analise os registros oficiais lado a lado, com a cobertura indicada para cada dado." />
    <section className="comparison-content" aria-label="Comparação de representantes">
      <div className="panel comparison-selection-panel">
        <div className="comparison-toolbar">
          <div className="comparison-selection-area">
            <div className="comparison-selection-heading"><strong>{selection.length} de 4 selecionados</strong><span>Compare representantes do mesmo cargo e da mesma Casa legislativa.</span></div>
            {selection.length > 0 && <ul className="comparison-selection-list" aria-label="Políticos selecionados">
              {selection.map((person) => <li className="comparison-pill" key={person.id}>
                <img src={person.photo || '/static/avatar.svg'} alt="" onError={(event) => { event.currentTarget.src = '/static/avatar.svg'; }} />
                <span>{person.name}</span>
                <Button variant="unstyled" type="button" aria-label={`Remover ${person.name}`} onClick={() => remove(person.id)}>×</Button>
              </li>)}
            </ul>}
          </div>
          <div className="comparison-period-controls">
            <SelectField label="Período" aria-label="Período da comparação" value={selectedValue} onValueChange={changePeriod}>
              <option value="todos">Todos os mandatos</option>
              {years.map((availableYear) => <option key={availableYear} value={String(availableYear)}>{availableYear}</option>)}
            </SelectField>
            {selection.length > 0 && <Button type="button" onClick={clear}>Limpar seleção</Button>}
          </div>
        </div>

        <div className="comparison-picker-control">
          <div className="comparison-picker-toolbar">
            <p>Monte sua seleção com até quatro representantes.</p>
            <Button variant="primary" type="button" aria-expanded={pickerOpen} onClick={() => { setPickerOpen((open) => !open); setSelectionError(''); }}>
              {pickerOpen ? 'Fechar busca' : '＋ Adicionar político'}
            </Button>
          </div>
          {pickerOpen && <div className="comparison-picker">
            <TextField label="Buscar político" type="search" value={search} onValueChange={setSearch} placeholder="Digite um nome ou partido" autoComplete="off" />
            {selectionError && <p className="comparison-selection-error" role="alert">{selectionError}</p>}
            {debouncedSearch.length < 2 ? <p className="small">Digite ao menos dois caracteres para buscar.</p>
              : pickerQuery.isLoading ? <Loading label="Buscando políticos…" />
                : pickerQuery.error ? <RequestError message={pickerQuery.error.message} />
                  : pickerResults.length ? <ul className="comparison-picker-results">
                    {pickerResults.map((person) => {
                      const incompatible = selection.some((selected) => selected.source && person.source && (selected.source !== person.source || selected.role !== person.role));
                      return <li key={person.id}><button type="button" disabled={incompatible || selection.length >= 4} onClick={() => addPerson(person)}>
                        <img src={person.photo || '/static/avatar.svg'} alt="" onError={(event) => { event.currentTarget.src = '/static/avatar.svg'; }} />
                        <span><strong>{person.name}</strong><small>{person.party || 'Partido não informado'} · {person.state || 'UF não informada'} · {person.source === 'senado' ? 'Senado Federal' : 'Câmara dos Deputados'}</small></span>
                        <span className="comparison-picker-action">{incompatible ? 'Cargo/Casa incompatível' : 'Adicionar'}</span>
                      </button></li>;
                    })}
                  </ul> : <p className="small">Nenhum político encontrado.</p>}
          </div>}
        </div>
        {selectionError && !pickerOpen && <p className="comparison-selection-error" role="alert">{selectionError}</p>}
      </div>

      {selection.length === 0 ? <EmptyState title="Adicione políticos à comparação" description="Use “Adicionar político” ou marque representantes na tela de políticos. Você pode comparar de dois a quatro nomes da mesma Casa e cargo." to="/politicos" action="Explorar políticos" />
        : selection.length < 2 ? <EmptyState title="Selecione mais um político" description="A comparação lado a lado fica disponível depois de adicionar pelo menos dois representantes compatíveis." to="/politicos" action="Voltar à lista" />
          : query.isLoading ? <Loading label="Calculando a comparação…" />
            : query.error ? <RequestError message={query.error.message} />
              : query.data ? <>
                {query.data.items.length ? <ComparisonMatrix items={query.data.items} selection={selectionById} metrics={metrics} reference={query.data.reference ?? null} year={query.data.year} allMandates={allMandates} onRemove={remove} />
                  : <EmptyState title="Sem dados para comparar" description={allMandates ? 'Não há registros de mandatos de exercício efetivo para os políticos selecionados.' : 'Os políticos selecionados não possuem indicadores compatíveis para este ano.'} />}
                {query.data.reference && <aside className="panel comparison-reference">
                  <div><h2>Média de referência · {query.data.reference.house}</h2><p>Calculada com parlamentares em exercício da mesma Casa e cargo cuja cobertura foi registrada.</p></div>
                  <div className="comparison-reference-grid">
                    <span>Despesas<strong>{money(query.data.reference.expenses)}</strong></span>
                    <span>Proposições<strong>{number(query.data.reference.proposals)}</strong></span>
                    <span>Votações nominais<strong>{number(query.data.reference.votes)}</strong></span>
                    <span>Emendas pagas<strong>{money(query.data.reference.amendments_paid)}</strong></span>
                  </div>
                </aside>}
                <PanelSources items={query.data.items} allMandates={allMandates} />
                <DataNote>{query.data.note}</DataNote>
              </> : null}
    </section>
  </div>;
}

function ComparisonMatrix({ items, selection, metrics, reference, year, allMandates, onRemove }: {
  items: Compared[];
  selection: Map<number, Selection>;
  metrics: MetricDefinition[];
  reference: Reference | null;
  year: number | null;
  allMandates: boolean;
  onRemove: (id: number) => void;
}) {
  const orderedItems = items.map((item) => ({ ...selection.get(item.id), ...item }));
  return <div className="panel comparison-results">
    <div className="comparison-results-heading"><div><h2>Comparação lado a lado</h2><p>{allMandates ? 'Totais nos anos de exercício efetivo registrados.' : `Indicadores do ano de ${year ?? currentYear}.`}</p></div><span>{orderedItems.length} políticos</span></div>
    <p className="comparison-scroll-cue">Deslize a tabela para ver todos os indicadores.</p>
    <div className="comparison-matrix-scroll" role="region" aria-label="Tabela comparativa de indicadores" tabIndex={0}>
      <div className={`comparison-matrix comparison-matrix--${orderedItems.length}`} role="table" aria-label="Indicadores por representante">
        <div className="comparison-profiles" role="rowgroup">
          <div className="comparison-profile-row" role="row">
            <div className="comparison-profile-spacer" role="presentation" aria-hidden="true" />
            {orderedItems.map((person) => <div className="comparison-profile-cell" role="columnheader" key={person.id}>
              <article className="comparison-profile">
                <img src={person.photo || '/static/avatar.svg'} alt={`Foto de ${person.name}`} onError={(event) => { event.currentTarget.src = '/static/avatar.svg'; }} />
                <div className="comparison-profile-content">
                  <h3><Link to={`/perfil/${person.id}`}>{person.name}</Link></h3>
                  <p className="comparison-profile-party">{person.party || 'Partido não informado'} · {person.state || 'UF não informada'}</p>
                  <p className="comparison-profile-role">{person.role}</p>
                  {allMandates && <p className="comparison-years"><strong>Anos considerados:</strong> {person.mandate_years?.length ? person.mandate_years.join(', ') : 'Sem anos de exercício registrados'}</p>}
                  {person.source_url
                    ? <a className="comparison-profile-source" href={publicSourcePage(person.source_url) ?? undefined} target="_blank" rel="noreferrer" aria-label={`Abrir cadastro oficial de ${person.name}`} title="Abrir cadastro oficial">↗</a>
                    : <span className="comparison-profile-source-missing" title="Cadastro oficial não informado" aria-label="Cadastro oficial não informado">—</span>}
                </div>
                <Button variant="unstyled" className="comparison-remove" type="button" aria-label={`Remover ${person.name}`} onClick={() => onRemove(person.id)}>×</Button>
              </article>
            </div>)}
          </div>
        </div>
        <div className="comparison-metrics" role="rowgroup">
          {metrics.map((metric) => {
            const values = orderedItems.map((person) => metric.getValue(person)).filter((value): value is number => value !== null && Number.isFinite(value));
            const maximum = Math.max(...values, 0);
            return <div className="comparison-metric-row" role="row" key={metric.key}>
              <div className="comparison-metric-label" role="rowheader"><span className="comparison-metric-icon"><metric.icon aria-hidden="true" /></span><span><strong>{metric.title}</strong><small>{metric.description}</small></span></div>
              {orderedItems.map((person) => {
                const value = metric.getValue(person);
                const relative = value !== null && maximum > 0 ? Math.round((value / maximum) * 100) : 0;
                const average = reference ? metric.reference(reference) : null;
                const referencePercent = !allMandates && metric.complete(person) && value !== null && average != null && average > 0 ? Math.round((value / average) * 100) : null;
                const coverageText = referencePercent === null ? metric.coverage(person) : `${referencePercent}% da média da ${reference?.house ?? 'referência'}`;
                return <div className="comparison-value-cell" role="cell" key={`${person.id}-${metric.key}`}>
                  <strong className="comparison-value">{metric.format(value)}</strong>
                  {value !== null && <ProgressBar className="comparison-value-meter" value={relative} label={`${metric.title}: ${relative}% do maior valor exibido`} />}
                  <div className="comparison-cell-details">
                    <small className="comparison-coverage">{coverageText}</small>
                    {metric.source(person) && <SourceLink href={metric.source(person) ?? null}>Fonte oficial</SourceLink>}
                  </div>
                </div>;
              })}
            </div>;
          })}
        </div>
      </div>
    </div>
  </div>;
}

function PanelSources({ items, allMandates }: { items: Compared[]; allMandates: boolean }) {
  const sources = [...new Set(items.map((person) => formatSource(person.source)))];
  return <aside className="panel comparison-sources">
    <div><h2>Fontes dos dados</h2>
      <p>{sources.join(' · ')}. A cobertura e os períodos variam conforme o conjunto publicado para cada parlamentar.</p>
      <p>{allMandates ? 'Os totais somam os anos-calendário que coincidem com períodos de exercício efetivo registrados. Cada indicador informa sua cobertura; não há média de referência para períodos agregados.' : 'As barras comparam os valores exibidos. Os percentuais em relação à média usam parlamentares em exercício da mesma Casa e cargo com cobertura registrada.'}</p>
    </div>
    <Link to="/fontes">Saiba mais sobre fontes e cobertura →</Link>
  </aside>;
}
